import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPredictiveRisks,
  fetchPredictiveRiskSummary,
  fetchTopPredictiveRisks,
} from '../lib/networkIntelligenceApi';
import { networkIntelligenceQueryKeys } from '../lib/networkIntelligenceQueryKeys';
import { sortBySeverityAndConfidence } from '../lib/networkIntelligenceFormatters';

export const usePredictiveRisks = () =>
  useQuery({
    queryKey: networkIntelligenceQueryKeys.risks,
    queryFn: fetchPredictiveRisks,
    refetchInterval: 15000,
  });

export const usePredictiveRiskSummary = () =>
  useQuery({
    queryKey: networkIntelligenceQueryKeys.summary,
    queryFn: fetchPredictiveRiskSummary,
    refetchInterval: 15000,
  });

export const useTopPredictiveRisks = (limit = 10) =>
  useQuery({
    queryKey: networkIntelligenceQueryKeys.top(limit),
    queryFn: () => fetchTopPredictiveRisks(limit),
    refetchInterval: 15000,
  });

export const useNetworkIntelligenceData = () => {
  const [riskType, setRiskType] = useState('ALL');
  const [severity, setSeverity] = useState('ALL');
  const [scopeType, setScopeType] = useState('ALL');
  const [area, setArea] = useState('');
  const [confidenceThreshold, setConfidenceThreshold] = useState(0);

  const risksQuery = usePredictiveRisks();
  const summaryQuery = usePredictiveRiskSummary();
  const topQuery = useTopPredictiveRisks(8);

  const rawItems = useMemo(() => risksQuery.data?.items || [], [risksQuery.data?.items]);
  const filteredItems = useMemo(() => {
    return sortBySeverityAndConfidence(rawItems).filter((item) => {
      if (riskType !== 'ALL' && String(item?.riskType || '').toUpperCase() !== riskType) return false;
      if (severity !== 'ALL' && String(item?.severity || '').toUpperCase() !== severity) return false;
      if (scopeType !== 'ALL' && String(item?.scopeType || '').toUpperCase() !== scopeType) return false;
      if (area.trim()) {
        const areaName = String(item?.areaName || '').toLowerCase();
        if (!areaName.includes(area.trim().toLowerCase())) return false;
      }
      const confidence = Number(item?.confidence || 0);
      if (confidence < Number(confidenceThreshold || 0)) return false;
      return true;
    });
  }, [rawItems, riskType, severity, scopeType, area, confidenceThreshold]);

  const isLoading = risksQuery.isLoading || summaryQuery.isLoading || topQuery.isLoading;
  const error = risksQuery.error || summaryQuery.error || topQuery.error;

  const refetch = () => {
    risksQuery.refetch();
    summaryQuery.refetch();
    topQuery.refetch();
  };

  return {
    summary: summaryQuery.data || {},
    risks: filteredItems,
    topRisks: topQuery.data?.items || [],
    total: filteredItems.length,
    filters: {
      riskType,
      setRiskType,
      severity,
      setSeverity,
      scopeType,
      setScopeType,
      area,
      setArea,
      confidenceThreshold,
      setConfidenceThreshold,
    },
    isLoading,
    error,
    refetch,
  };
};
